import { useCallback, useEffect, useRef, useState } from "react";
import { MonoLabel } from "./MonoLabel";
import {
  OKOSHI_TRANSCRIBE_MODELS,
  OKOSHI_VAULT_MODES,
  TSUYAKU_LANGUAGE_LABELS,
  TSUYAKU_OUTPUT_LANGUAGES,
  ZASSO_VAULT_MODES,
  ZASSO_VOICES,
  type OkoshiTranscribeModel,
  type OkoshiVaultMode,
  type Settings,
  type TsuyakuOutputLanguage,
  type ZassoVaultMode,
  type ZassoVoice,
} from "../lib/api-types";
import {
  enumerateAudioDevices,
  onDeviceChange,
  supportsSinkId,
  type AudioDeviceInfo,
} from "../lib/devices";
import {
  readSelectedDevices,
  writeSelectedInput,
  writeSelectedOutput,
  SYSTEM_DEFAULT_DEVICE_ID,
  type SelectedDevices,
} from "../lib/deviceStorage";

/**
 * SettingsPanel — Phase 5 wired up the Okoshi-specific settings; Phase 6
 * extends the same pattern to the Zasso voice + vault policy.
 */
export function SettingsPanel({
  onClose,
  onSettingsChanged,
  onDevicesChanged,
}: {
  onClose: () => void;
  onSettingsChanged?: (settings: Settings) => void;
  onDevicesChanged?: (devices: SelectedDevices) => void;
}) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<keyof Settings | null>(null);
  const [busyKey, setBusyKey] = useState<keyof Settings | null>(null);
  const hintTimerRef = useRef<number | null>(null);

  // ── Device enumeration state (Phase 3) ─────────────────────
  const [devices, setDevices] = useState<{
    inputs: AudioDeviceInfo[];
    outputs: AudioDeviceInfo[];
  }>({ inputs: [], outputs: [] });
  const [selectedDevices, setSelectedDevices] = useState<SelectedDevices>(
    () => readSelectedDevices(),
  );
  const canChangeOutput = supportsSinkId();

  // ── Load current settings on mount ───────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/settings");
        if (!res.ok) throw new Error(`settings GET ${res.status}`);
        const data = (await res.json()) as Settings;
        if (!cancelled) setSettings(data);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
      if (hintTimerRef.current !== null) {
        window.clearTimeout(hintTimerRef.current);
      }
    };
  }, []);

  // ── Enumerate audio devices + subscribe to changes (Phase 3) ──
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const list = await enumerateAudioDevices();
      if (!cancelled) setDevices(list);
    };
    void refresh();
    const unsubscribe = onDeviceChange(() => {
      void refresh();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const pickInput = useCallback(
    (raw: string) => {
      const id = raw === SYSTEM_DEFAULT_DEVICE_ID ? null : raw;
      writeSelectedInput(id);
      setSelectedDevices((prev) => {
        const next = { ...prev, inputId: id };
        onDevicesChanged?.(next);
        return next;
      });
    },
    [onDevicesChanged],
  );

  const pickOutput = useCallback(
    (raw: string) => {
      const id = raw === SYSTEM_DEFAULT_DEVICE_ID ? null : raw;
      writeSelectedOutput(id);
      setSelectedDevices((prev) => {
        const next = { ...prev, outputId: id };
        onDevicesChanged?.(next);
        return next;
      });
    },
    [onDevicesChanged],
  );

  // ── PUT a single field, optimistic update + rollback on failure ──
  const patchSetting = useCallback(
    async <K extends keyof Settings>(key: K, value: Settings[K]) => {
      if (!settings) return;
      const previous = settings;
      const optimistic: Settings = { ...settings, [key]: value };
      setSettings(optimistic);
      setBusyKey(key);
      setLoadError(null);
      try {
        const res = await fetch("/api/settings", {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ [key]: value }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`settings PUT ${res.status} ${text.slice(0, 120)}`);
        }
        const server = (await res.json()) as Settings;
        setSettings(server);
        onSettingsChanged?.(server);
        setSavedHint(key);
        if (hintTimerRef.current !== null) {
          window.clearTimeout(hintTimerRef.current);
        }
        hintTimerRef.current = window.setTimeout(() => {
          setSavedHint((h) => (h === key ? null : h));
          hintTimerRef.current = null;
        }, 1200);
      } catch (err) {
        setSettings(previous); // rollback
        setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyKey(null);
      }
    },
    [settings, onSettingsChanged],
  );

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-ink/30 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onClick={onClose}
    >
      <div
        className="flex h-[86vh] w-full max-w-md flex-col rounded-t-box border border-ink/15 bg-cream shadow-xl sm:h-auto sm:max-h-[86vh] sm:rounded-box"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-ink/15 px-4 py-3">
          <div>
            <div className="font-mono text-[10px] tracking-[0.18em] text-subtext">
              SIGNAL · STORAGE
            </div>
            <h2 className="koe-hand text-2xl">settings</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[11px] tracking-[0.18em] text-subtext hover:text-ink"
          >
            ✕ CLOSE
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-5">
          <Section title="AUDIO · I/O">
            <DeviceSelectRow
              label="INPUT"
              value={selectedDevices.inputId ?? SYSTEM_DEFAULT_DEVICE_ID}
              devices={devices.inputs}
              disabled={false}
              onChange={pickInput}
            />
            <DeviceSelectRow
              label="OUTPUT"
              value={selectedDevices.outputId ?? SYSTEM_DEFAULT_DEVICE_ID}
              devices={devices.outputs}
              disabled={!canChangeOutput}
              onChange={pickOutput}
            />
            <p className="pt-1 font-mono text-[10px] leading-relaxed text-subtext">
              {canChangeOutput
                ? "changes apply on the next session."
                : "iOS Safari: output selection is unavailable. The OS audio routing is followed — switch there instead."}
            </p>
            {devices.inputs.length > 0 &&
              devices.inputs.every((d) => d.label.length === 0) && (
                <p className="pt-1 font-mono text-[10px] text-subtext">
                  device labels show up after the first mic permission. Start any session once to see them.
                </p>
              )}
          </Section>

          <Section title="OKOSHI · SETTINGS">
            <SelectRow
              label="MODEL"
              value={settings?.okoshiTranscribeModel ?? ""}
              disabled={!settings || busyKey === "okoshiTranscribeModel"}
              saved={savedHint === "okoshiTranscribeModel"}
              description={
                settings
                  ? MODEL_DESCRIPTIONS[settings.okoshiTranscribeModel]
                  : "loading…"
              }
              onChange={(v) =>
                patchSetting(
                  "okoshiTranscribeModel",
                  v as OkoshiTranscribeModel,
                )
              }
              options={OKOSHI_TRANSCRIBE_MODELS.map((m) => ({
                value: m,
                label: m,
                hint: MODEL_DESCRIPTIONS[m],
              }))}
            />
            <SelectRow
              label="POLICY"
              value={settings?.okoshiVaultMode ?? ""}
              disabled={!settings || busyKey === "okoshiVaultMode"}
              saved={savedHint === "okoshiVaultMode"}
              description={
                settings
                  ? VAULT_DESCRIPTIONS[settings.okoshiVaultMode]
                  : "loading…"
              }
              onChange={(v) =>
                patchSetting("okoshiVaultMode", v as OkoshiVaultMode)
              }
              options={OKOSHI_VAULT_MODES.map((m) => ({
                value: m,
                label: m,
                hint: VAULT_DESCRIPTIONS[m],
              }))}
            />
          </Section>

          <Section title="ZASSO · SETTINGS">
            <SelectRow
              label="VOICE"
              value={settings?.zassoVoice ?? ""}
              disabled={!settings || busyKey === "zassoVoice"}
              saved={savedHint === "zassoVoice"}
              description={
                settings ? VOICE_DESCRIPTIONS[settings.zassoVoice] : "loading…"
              }
              onChange={(v) => patchSetting("zassoVoice", v as ZassoVoice)}
              options={ZASSO_VOICES.map((v) => ({
                value: v,
                label: v,
                hint: VOICE_DESCRIPTIONS[v],
              }))}
            />
            <SelectRow
              label="POLICY"
              value={settings?.zassoVaultMode ?? ""}
              disabled={!settings || busyKey === "zassoVaultMode"}
              saved={savedHint === "zassoVaultMode"}
              description={
                settings
                  ? VAULT_DESCRIPTIONS[settings.zassoVaultMode]
                  : "loading…"
              }
              onChange={(v) =>
                patchSetting("zassoVaultMode", v as ZassoVaultMode)
              }
              options={ZASSO_VAULT_MODES.map((m) => ({
                value: m,
                label: m,
                hint: VAULT_DESCRIPTIONS[m],
              }))}
            />
          </Section>

          <Section title="TSUYAKU · SETTINGS">
            <SelectRow
              label="TARGET"
              value={settings?.tsuyakuOutputLanguage ?? ""}
              disabled={!settings || busyKey === "tsuyakuOutputLanguage"}
              saved={savedHint === "tsuyakuOutputLanguage"}
              description={
                settings
                  ? `→ ${TSUYAKU_LANGUAGE_LABELS[settings.tsuyakuOutputLanguage]} · source auto-detected`
                  : "loading…"
              }
              onChange={(v) =>
                patchSetting(
                  "tsuyakuOutputLanguage",
                  v as TsuyakuOutputLanguage,
                )
              }
              options={TSUYAKU_OUTPUT_LANGUAGES.map((lang) => ({
                value: lang,
                label: `${lang} · ${TSUYAKU_LANGUAGE_LABELS[lang]}`,
                hint: TSUYAKU_LANGUAGE_LABELS[lang],
              }))}
            />
            <p className="pt-1 font-mono text-[10px] leading-relaxed text-subtext">
              voice adapts to speaker · 13 target languages supported by gpt-realtime-translate.
            </p>
          </Section>

          <Section title="SESSION · GUARDS">
            <Row label="MAX_SESSION">15:00</Row>
            <Row label="SILENCE_TIMEOUT">3:00</Row>
          </Section>

          {loadError && (
            <div
              role="alert"
              className="rounded-box border border-rust bg-cream-soft px-3 py-2 font-mono text-[10px] leading-relaxed text-ink"
            >
              <span className="text-rust">ERR · </span>
              {loadError}
            </div>
          )}
        </div>

        <footer className="border-t border-ink/15 px-4 py-3">
          <p className="font-mono text-[10px] tracking-wider text-subtext">
            changes apply on the next REC session
          </p>
        </footer>
      </div>
    </div>
  );
}

// ── Inline copy for UI hints ───────────────────────────────────
const MODEL_DESCRIPTIONS: Record<OkoshiTranscribeModel, string> = {
  "gpt-4o-mini-transcribe": "fast · low-cost",
  "gpt-4o-transcribe": "balanced · default",
  "gpt-realtime-whisper": "new model · experimental",
};

const VAULT_DESCRIPTIONS: Record<OkoshiVaultMode | ZassoVaultMode, string> = {
  "auto-save": "save immediately on stop",
  "ask-each-time": "review and save manually · default",
  never: "no save · copy or download only",
};

const VOICE_DESCRIPTIONS: Record<ZassoVoice, string> = {
  alloy: "balanced · neutral",
  verse: "dynamic · expressive · default",
  coral: "warm · soft",
  sage: "calm · considered",
  ash: "dry · understated",
  ballad: "melodic · flowing",
  cedar: "grounded · measured",
  marin: "bright · energetic",
};

// ── Small pieces ───────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="font-mono text-[10px] tracking-[0.18em] text-subtext">
        {title}
      </div>
      <div className="space-y-2 border-t border-dashed border-rule pt-2">
        {children}
      </div>
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <MonoLabel label={label}>{children}</MonoLabel>
    </div>
  );
}

interface SelectOption {
  value: string;
  label: string;
  hint: string;
}

function SelectRow({
  label,
  value,
  description,
  options,
  disabled,
  saved,
  onChange,
}: {
  label: string;
  value: string;
  description: string;
  options: SelectOption[];
  disabled: boolean;
  saved: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] tracking-wider text-subtext">
          {label}
        </span>
        <span className="text-subtext">·</span>
        <div className="relative flex-1">
          <select
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className={[
              "w-full appearance-none rounded-box border border-ink/25 bg-cream-soft",
              "py-1.5 pl-2 pr-8 font-mono text-[12px] text-ink",
              "focus:border-rust focus:outline-none",
              disabled ? "opacity-60" : "hover:border-ink/50",
            ].join(" ")}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span
            aria-hidden
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-subtext"
          >
            ▾
          </span>
        </div>
        <span
          className={[
            "shrink-0 font-mono text-[10px] tracking-widest transition-opacity",
            saved ? "text-rust opacity-100" : "opacity-0",
          ].join(" ")}
          aria-live="polite"
        >
          saved ✓
        </span>
      </div>
      <p className="pl-8 font-mono text-[10px] text-subtext">└ {description}</p>
    </div>
  );
}

function DeviceSelectRow({
  label,
  value,
  devices,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  devices: AudioDeviceInfo[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  // Resolve current selection's description (label or hint line).
  const current = devices.find((d) => d.deviceId === value);
  const description =
    value === SYSTEM_DEFAULT_DEVICE_ID
      ? "follow the OS default"
      : current
        ? current.label || "unnamed device"
        : "not connected · will fall back to the system default";

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] tracking-wider text-subtext">
          {label}
        </span>
        <span className="text-subtext">·</span>
        <div className="relative flex-1">
          <select
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className={[
              "w-full appearance-none rounded-box border border-ink/25 bg-cream-soft",
              "py-1.5 pl-2 pr-8 font-mono text-[12px] text-ink",
              "focus:border-rust focus:outline-none",
              disabled ? "opacity-60" : "hover:border-ink/50",
            ].join(" ")}
          >
            <option value={SYSTEM_DEFAULT_DEVICE_ID}>system default</option>
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {deviceOptionLabel(d)}
              </option>
            ))}
          </select>
          <span
            aria-hidden
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-subtext"
          >
            ▾
          </span>
        </div>
      </div>
      <p className="pl-8 font-mono text-[10px] text-subtext">└ {description}</p>
    </div>
  );
}

function deviceOptionLabel(d: AudioDeviceInfo): string {
  const label = d.label && d.label.length > 0 ? d.label : "(unnamed device)";
  return d.isBluetooth ? `◦ BT · ${label}` : label;
}
